"use client";

import { FormEvent, useMemo, useState } from "react";
import styles from "./EpexCalculator.module.css";

type IndexResult = {
  index: string;
  period: string;
  hourCount: number;
  averageEurPerMWh: number;
  averageEurPerKWh: number;
  source: string;
  calculation: string;
};

type FetchState = "idle" | "loading" | "error" | "success";

const defaultPeriod = () => {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}`;
};

const formatMonthLabel = (period: string) => {
  const [year, month] = period.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
};

export default function EpexCalculator() {
  const [period, setPeriod] = useState<string>(defaultPeriod);
  const [result, setResult] = useState<IndexResult | null>(null);
  const [status, setStatus] = useState<FetchState>("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const label = useMemo(() => formatMonthLabel(period), [period]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("loading");
    setErrorMessage("");
    setResult(null);

    try {
      const response = await fetch(`/api/indexes/epexdamrek?period=${period}`);
      const payload = await response.json();

      if (!response.ok) {
        const detail = payload?.error || "Unexpected response";
        throw new Error(detail);
      }

      setResult(payload as IndexResult);
      setStatus("success");
    } catch (error) {
      setStatus("error");
      setErrorMessage((error as Error).message || "Unable to calculate index value.");
    }
  };

  return (
    <div className={styles.calculator}>
      <div className={styles.cardHeader}>
        <div>
          <p className={styles.metaLabel}>Index calculator</p>
          <h3 className={styles.title}>EPEXDAMREK monthly value</h3>
          <p className={styles.helper}>Arithmetic mean of all Belgian day-ahead hourly prices for the month.</p>
        </div>
        <span className={styles.badge}>Live fetch</span>
      </div>

      <form onSubmit={handleSubmit} className={styles.formArea}>
        <div className={styles.inputBlock}>
          <label htmlFor="period" className="form-label">
            Target month
          </label>
          <input
            id="period"
            name="period"
            type="month"
            className="form-control"
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
            required
          />
          <small className={styles.hint}>Pulls data from energy-charts.info and rebuilds the index.</small>
        </div>
        <div className={styles.buttonBlock}>
          <button className="btn btn-primary" type="submit" disabled={status === "loading"}>
            {status === "loading" ? "Calculating…" : "Calculate"}
          </button>
          <span className={styles.periodLabel}>{label}</span>
        </div>
      </form>

      {status === "error" && <div className={styles.errorBox}>{errorMessage}</div>}

      {status === "success" && result && (
        <div className={styles.resultPanel}>
          <div className={styles.resultRow}>
            <div>
              <p className={styles.metaLabel}>Average price</p>
              <p className={styles.metric}>{result.averageEurPerMWh} €/MWh</p>
              <p className={styles.metricSmall}>{result.averageEurPerKWh} €/kWh</p>
            </div>
            <div>
              <p className={styles.metaLabel}>Hours included</p>
              <p className={styles.metric}>{result.hourCount}</p>
            </div>
            <div className={styles.sourceBlock}>
              <p className={styles.metaLabel}>Source</p>
              <a href={result.source} target="_blank" rel="noreferrer">
                energy-charts.info
              </a>
            </div>
          </div>
          <p className={styles.calculationNote}>{result.calculation}</p>
        </div>
      )}
    </div>
  );
}
